import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Chip,
  Autocomplete,
} from "@mui/material";
import { GridFilterItem, GridLogicOperator } from "@mui/x-data-grid-pro";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import { TFunction } from "i18next";
import { customFilterFormStyles } from "../dataTable/table/customFilterForm/CustomFilterFormStyles";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../configureStore";
import {
  fetchSBSites,
  fetchSBActionsNames,
} from "../../redux/actions/users/userAsyncActions";

interface CustomFilterFormProps {
  columns: any[];
  filterModel: {
    items: GridFilterItem[];
    logicOperator?: GridLogicOperator;
  };
  onFilterChange: (newFilterModel: {
    items: GridFilterItem[];
    logicOperator: GridLogicOperator;
  }) => void;
  onApplyFilters: () => void;
  onClose?: () => void;
  t: TFunction<"global", undefined>;
  preselectedField?: string | null;
  setPreselectedFilterField: (field: string | null) => void;
}

const getColumnType = (columns: any[], field: string) => {
  const column = columns.find((col) => col.field === field);
  return column ? column.columnType : "";
};

const CustomFilterForm: React.FC<CustomFilterFormProps> = ({
  columns,
  filterModel,
  onFilterChange,
  onApplyFilters,
  onClose,
  t,
  preselectedField,
  setPreselectedFilterField,
}) => {
  const [filterItems, setFilterItems] = useState<any[]>([
    {
      field: "",
      operator: "",
      value: "",
      column: "",
      realOperator: "",
      columnType: "",

      filter: "",
      service: "",
      queryFormat: "",
    },
  ]);
  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({});
  const [apiOptions, setApiOptions] = useState<{ [key: number]: any[] }>({});

  const formRef = React.useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const locations = useSelector(
    (state: RootState) =>
      state.CreateTablesUsersReducer.usersSBFilterValues.locations,
  );

  const actions = useSelector(
    (state: RootState) =>
      state.CreateTablesUsersReducer.usersSBFilterValues.actions,
  );
  const loadingFilters = useSelector(
    (state: RootState) =>
      state.CreateTablesUsersReducer.loadingUsersSbFiltersValues,
  );

  const getTranslatedOperatorLabel = (operatorValue: string) => {
    switch (operatorValue) {
      case "$ilike":
        return t("custom_filter.operators.contains");
      case "$eq":
        return t("custom_filter.operators.equals");
      case "$gt":
        return t("custom_filter.operators.greater_than");
      case "$lt":
        return t("custom_filter.operators.less_than");
      case "$ne":
        return t("custom_filter.operators.not_equal");
      case "$in":
        return t("custom_filter.operators.is_any_of");
      default:
        return operatorValue;
    }
  };

  // Load current filter data on initialization
  useEffect(() => {
    if (filterModel?.items?.length > 0) {
      setFilterItems(filterModel.items);
    } else {
      setFilterItems([
        {
          field: "",
          operator: "",
          value: "",
          column: "",
          realOperator: "",
          columnType: "",
          filter: "",
          service: "",
          queryFormat: "",
        },
      ]);
    }
  }, [filterModel]);

  // Convert IDs back to names for API/LOCAL filters when locations/actions are loaded
  useEffect(() => {
    if (
      (locations && locations.length > 0 && locations[0]?.id !== undefined) ||
      (actions && actions.length > 0 && actions[0]?.id !== undefined)
    ) {
      setFilterItems((prevItems) =>
        prevItems.map((item) => {
          const column = columns.find((col) => col.field === item.field);

          // Handle API filter (locations)
          if (
            column?.filter === "API" &&
            item.operator === "$in" &&
            item.value &&
            locations &&
            locations.length > 0
          ) {
            const queryFormat = item.queryFormat || column?.queryFormat;
            if (
              queryFormat &&
              typeof item.value === "string" &&
              item.value.includes(queryFormat)
            ) {
              // Extract IDs from query string
              const matches = item.value.match(/=(\d+)/g);
              if (matches) {
                const ids = matches.map((match) => Number(match.substring(1)));
                const names = ids
                  .map((id) => locations.find((loc) => loc.id === id)?.name)
                  .filter((name) => name !== undefined);

                if (names.length > 0) {
                  return {
                    ...item,
                    value: names,
                  };
                }
              }
              return item;
            }

            // Check if the value is an array or string
            let values: any[];
            if (Array.isArray(item.value)) {
              values = item.value;
            } else {
              values = item.value.split(",").map((v) => v.trim());
            }

            const firstValue = values[0];

            // If first value is a number, assume all are IDs and convert to names
            if (firstValue && !Number.isNaN(Number(firstValue))) {
              const names = values
                .map(
                  (id) => locations.find((loc) => loc.id === Number(id))?.name,
                )
                .filter((name) => name !== undefined);

              if (names.length > 0) {
                return {
                  ...item,
                  value: names,
                };
              }
            }
          }

          // Handle LOCAL filter (actions)
          if (
            column?.filter === "LOCAL" &&
            item.operator === "$in" &&
            item.value &&
            actions &&
            actions.length > 0
          ) {
            // Check if the value is an array or string
            let values: any[];
            if (Array.isArray(item.value)) {
              values = item.value;
            } else {
              values = item.value.split(",").map((v) => v.trim());
            }

            const firstValue = values[0];

            // If first value is a number, assume all are IDs and convert to names with cerealType
            if (firstValue && !Number.isNaN(Number(firstValue))) {
              const formattedNames = values
                .map((id) => {
                  const action = actions.find((act) => act.id === Number(id));
                  if (action) {
                    return action.cerealType
                      ? `${action.name} (${action.cerealType})`
                      : action.name;
                  }
                  return undefined;
                })
                .filter((name) => name !== undefined);

              if (formattedNames.length > 0) {
                return {
                  ...item,
                  value: formattedNames,
                };
              }
            }

            // If values are plain names (not formatted), convert them to formatted display
            if (
              firstValue &&
              typeof firstValue === "string" &&
              Number.isNaN(Number(firstValue))
            ) {
              const formattedNames = values
                .map((name) => {
                  // Extract the plain name if it's already formatted
                  const plainName = name.match(/^(.+?)\s*\(/)
                    ? name.match(/^(.+?)\s*\(/)[1]
                    : name;
                  const action = actions.find((act) => act.name === plainName);
                  if (action) {
                    return action.cerealType
                      ? `${action.name} (${action.cerealType})`
                      : action.name;
                  }
                  return name; // Keep original if not found
                })
                .filter((name) => name !== undefined);

              if (formattedNames.length > 0) {
                return {
                  ...item,
                  value: formattedNames,
                };
              }
            }
          }

          return item;
        }),
      );
    }
  }, [locations, actions, columns]);

  useEffect(() => {
    if (preselectedField) {
      setFilterItems((prev) => {
        // Check if there are existing valid filters
        const hasExistingFilters = prev.some(
          (item) => item.field && item.field !== "",
        );

        // Get column information
        const column = columns.find((col) => col.field === preselectedField);
        const columnType = getColumnType(columns, preselectedField);

        const newFilterItem = {
          field: preselectedField,
          operator: "",
          value: "",
          column: "",
          realOperator: "",
          columnType: columnType,
          filter: column?.filter || "",
          service: column?.service || "",
          queryFormat: column?.queryFormat || "",
        };

        if (!hasExistingFilters && prev.length === 1) {
          return [newFilterItem];
        } else {
          return [...prev, newFilterItem];
        }
      });

      setPreselectedFilterField(null);
    }
  }, [preselectedField, setPreselectedFilterField, columns]);

  // Fetch API/LOCAL options when a field with filter:"API" or filter:"LOCAL" is selected
  useEffect(() => {
    filterItems.forEach((item, index) => {
      if (item.field && item.operator === "$in") {
        const column = columns.find((col) => col.field === item.field);

        // Handle API filter for geoPoints (locations)
        if (column?.filter === "API" && column.service === "geoPoints") {
          // Check if we haven't already fetched or if locations is empty
          if (
            !apiOptions[index] &&
            (!locations ||
              locations.length === 0 ||
              locations[0]?.id === undefined)
          ) {
            dispatch(fetchSBSites());
          } else if (
            locations &&
            locations.length > 0 &&
            locations[0]?.id !== undefined
          ) {
            // Store the fetched locations for this filter index
            setApiOptions((prev) => ({ ...prev, [index]: locations }));
          }
        }

        // Handle LOCAL filter for actions
        if (column?.filter === "LOCAL" && column.service === "actions") {
          // Check if we haven't already fetched or if actions is empty
          if (
            !apiOptions[index] &&
            (!actions || actions.length === 0 || actions[0]?.id === undefined)
          ) {
            dispatch(fetchSBActionsNames());
          } else if (
            actions &&
            actions.length > 0 &&
            actions[0]?.id !== undefined
          ) {
            // Store the fetched actions for this filter index
            setApiOptions((prev) => ({ ...prev, [index]: actions }));
          }
        }
      }
    });
  }, [filterItems, columns, locations, actions, apiOptions, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (formRef.current && formRef.current.contains(target)) {
        return;
      }

      const isInMuiPopover =
        target &&
        (target as Element).closest(
          ".MuiPopover-root, .MuiMenu-root, .MuiSelect-popper, .MuiAutocomplete-popper",
        );
      if (isInMuiPopover) {
        return;
      }

      onClose?.();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleFieldChange = (index: number, field: string) => {
    const newItems = [...filterItems];
    const column = columns.find((col) => col.field === field);
    newItems[index] = {
      ...newItems[index],
      field,
      operator: "",
      value: "",
      column: "",
      realOperator: "",
      columnType: getColumnType(columns, field),
      filter: column?.filter || "",
      service: column?.service || "",
      queryFormat: column?.queryFormat || "",
    };
    setFilterItems(newItems);
  };

  const handleOperatorChange = (index: number, operator: string) => {
    // Update the operator for the specific filter item
    const newItems = [...filterItems];
    newItems[index] = {
      ...newItems[index],
      operator,
      realOperator: operator,
      column:
        columns.find((col) => col.field === newItems[index].field)
          ?.headerName || "",
      columnType: getColumnType(columns, newItems[index].field),
      filter: newItems[index].filter,
      service: newItems[index].service,
      queryFormat: newItems[index].queryFormat,
    };
    setFilterItems(newItems);
  };

  const handleValueChange = (index: number, value: string) => {
    const newItems = [...filterItems];
    newItems[index] = { ...newItems[index], value };
    setFilterItems(newItems);
  };

  const handleChipValueChange = (index: number, values: string[]) => {
    const newItems = [...filterItems];
    newItems[index] = { ...newItems[index], value: values };
    setFilterItems(newItems);
  };

  const getChipValues = (value: any) => {
    if (Array.isArray(value)) {
      return value;
    }
    return value
      ? value
          .split(",")
          .map((v: string) => v.trim())
          .filter(Boolean)
      : [];
  };

  const getAvailableOperators = (fieldName: string) => {
    if (!fieldName) return [];
    const column = columns.find((col) => col.field === fieldName);
    // Return available filter operators for the selected field
    return column?.filterOperators || [];
  };

  const addFilter = () => {
    setFilterItems([
      ...filterItems,
      {
        field: "",
        operator: "",
        value: "",
        column: "",
        realOperator: "",
        columnType: "",
        filter: "",
        service: "",
        queryFormat: "",
      },
    ]);
  };

  const removeFilter = (index: number) => {
    const newItems = filterItems.filter((_, i) => i !== index);
    setFilterItems(
      newItems.length > 0
        ? newItems
        : [
            {
              field: "",
              operator: "",
              value: "",
              column: "",
              realOperator: "",
              columnType: "",
              filter: "",
              service: "",
              queryFormat: "",
            },
          ],
    );
  };

  const handleApplyFilter = () => {
    const validItems = filterItems
      .filter((item) => item.field && item.operator)
      .map((item, index) => {
        const column = columns.find((col) => col.field === item.field);

        // Handle API filters - build query strings
        if (column?.filter === "API" && item.operator === "$in") {
          // Get the API options for this filter index
          const options = apiOptions[index] || [];
          // Convert the selected names to IDs
          const selectedNames = Array.isArray(item.value)
            ? item.value
            : item.value
              ? item.value.split(",").map((v) => v.trim())
              : [];
          const selectedIds = selectedNames
            .map((name) => options.find((opt) => opt.name === name)?.id)
            .filter((id) => id !== undefined);

          // Build the query string using queryFormat if available
          const queryFormat = item.queryFormat || column?.queryFormat;
          let formattedValue;

          if (queryFormat && selectedIds.length > 0) {
            // Build query
            formattedValue = selectedIds
              .map((id) => `${queryFormat}${id}`)
              .join("&");
          } else {
            formattedValue = selectedIds;
          }

          return {
            ...item,
            value: formattedValue,
            filter: item.filter || column?.filter,
            service: item.service || column?.service,
            queryFormat: item.queryFormat || column?.queryFormat,
          };
        }

        // Handle LOCAL filters - convert names to IDs for filtering
        if (column?.filter === "LOCAL" && item.operator === "$in") {
          // Get the LOCAL options for this filter index
          const options = apiOptions[index] || [];
          // Convert the selected formatted names to IDs
          const selectedNames = Array.isArray(item.value)
            ? item.value
            : item.value
              ? item.value.split(",").map((v) => v.trim())
              : [];

          // Extract both name and cerealType from "Name (CerealType)" format
          const selectedIds = selectedNames
            .map((displayName) => {
              // Extract name and cerealType from formatted string
              const match = displayName.match(/^(.+?)\s*\((.+?)\)$/);
              if (match) {
                const plainName = match[1];
                const cerealType = match[2];
                // Find by BOTH name and cerealType to get correct ID
                return options.find(
                  (opt) =>
                    opt.name === plainName && opt.cerealType === cerealType,
                )?.id;
              } else {
                // Fallback: no cerealType, search by name only
                return options.find((opt) => opt.name === displayName)?.id;
              }
            })
            .filter((id) => id !== undefined);

          return {
            ...item,
            value: selectedIds,
            filter: item.filter || column?.filter,
            service: item.service || column?.service,
          };
        }

        // For other filters, return as is
        return item;
      });

    const newFilterModel = {
      items: validItems,
      logicOperator: GridLogicOperator.And,
    };

    onFilterChange(newFilterModel);
    const hasApiFilters = validItems.some((item) => item.filter === "API");
    if (hasApiFilters) {
      onApplyFilters();
    }
  };

  const handleClearAll = () => {
    setFilterItems([
      {
        field: "",
        operator: "",
        value: "",
        column: "",
        realOperator: "",
        columnType: "",
        filter: "",
        service: "",
        queryFormat: "",
      },
    ]);

    const newFilterModel = {
      items: [],
      logicOperator: GridLogicOperator.And,
      column: "",
      realOperator: "",
      columnType: "",
      filter: "",
      service: "",
      queryFormat: "",
    };

    onFilterChange(newFilterModel);
    onApplyFilters();
  };

  const getFilterableColumns = () => {
    return columns.filter(
      (col) => col.filterable !== false && col.filterOperators?.length > 0,
    );
  };

  return (
    <Box
      ref={formRef}
      onClick={(e) => e.stopPropagation()}
      sx={customFilterFormStyles.container}
    >
      {/* Header row */}
      <Box sx={customFilterFormStyles.headerRow}>
        <Box></Box>
        <Typography variant="body2" sx={customFilterFormStyles.headerText}>
          {t("custom_filter.columns")}
        </Typography>
        <Typography variant="body2" sx={customFilterFormStyles.headerText}>
          {t("custom_filter.operator")}
        </Typography>
        <Typography variant="body2" sx={customFilterFormStyles.headerText}>
          {t("custom_filter.value")}
        </Typography>
      </Box>

      {filterItems.map((filterItem, index) => (
        <Box
          key={`filter-${index}-${filterItem.field}`}
          sx={customFilterFormStyles.filterRow}
        >
          <Button
            onClick={() => removeFilter(index)}
            disabled={filterItems.length === 0}
            sx={customFilterFormStyles.deleteButton}
          >
            ✕
          </Button>

          <Select
            value={filterItem.field}
            onChange={(e) => handleFieldChange(index, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            displayEmpty
            size="small"
            variant="standard"
            sx={customFilterFormStyles.select}
          >
            <MenuItem value="">
              <em style={customFilterFormStyles.placeholderText}>
                {t("custom_filter.select_field")}
              </em>
            </MenuItem>
            {getFilterableColumns().map((column) => (
              <MenuItem key={column.field} value={column.field}>
                {column.headerName || column.field}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={filterItem.operator || ""}
            onChange={(e) => handleOperatorChange(index, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            disabled={!filterItem.field}
            displayEmpty
            size="small"
            variant="standard"
            sx={customFilterFormStyles.select}
          >
            <MenuItem value="">
              <em style={customFilterFormStyles.placeholderText}>
                {t("custom_filter.operator")}
              </em>
            </MenuItem>
            {getAvailableOperators(filterItem.field).map((operator) => (
              <MenuItem key={operator.value} value={operator.value}>
                {getTranslatedOperatorLabel(operator.value)}
              </MenuItem>
            ))}
          </Select>

          {filterItem.realOperator === "$in" ? (
            (() => {
              const column = columns.find(
                (col) => col.field === filterItem.field,
              );
              const isApiFilter = column?.filter === "API";
              const isLocalFilter = column?.filter === "LOCAL";
              const isDynamicFilter = isApiFilter || isLocalFilter;
              const apiOptionsForIndex = apiOptions[index] || [];
              const isActionsFilter = column?.service === "actions";

              // Format options for display
              const formatOptionLabel = (opt: any) => {
                if (isActionsFilter && opt.cerealType) {
                  return `${opt.name} (${opt.cerealType})`;
                }
                return opt.name;
              };

              return (
                <Autocomplete
                  multiple
                  freeSolo
                  loading={isDynamicFilter && loadingFilters}
                  options={
                    isDynamicFilter
                      ? apiOptionsForIndex.map(formatOptionLabel)
                      : []
                  }
                  filterOptions={(options, state) => {
                    // For dynamic filters, filter normally
                    if (isDynamicFilter) {
                      return options.filter((option) =>
                        option
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase()),
                      );
                    }
                    // For freeSolo filters, allow any input
                    return [];
                  }}
                  value={getChipValues(filterItem.value)}
                  inputValue={inputValues[index] || ""}
                  onInputChange={(event, newInputValue, reason) => {
                    // Allow typing freely
                    if (reason === "input") {
                      setInputValues((prev) => ({
                        ...prev,
                        [index]: newInputValue,
                      }));
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    event?.stopPropagation();

                    // Handle selection from dropdown or chip removal
                    if (
                      reason === "selectOption" ||
                      reason === "removeOption" ||
                      reason === "clear"
                    ) {
                      handleChipValueChange(index, newValue);
                      setInputValues((prev) => ({ ...prev, [index]: "" }));
                    }
                    // Handle Enter key or blur (createOption)
                    else if (reason === "createOption") {
                      handleChipValueChange(index, newValue);
                      setInputValues((prev) => ({ ...prev, [index]: "" }));
                    }
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, tagIndex) => {
                      const displayLabel =
                        option.length > 10
                          ? `${option.substring(0, 10)}...`
                          : option;
                      return (
                        <Chip
                          variant="outlined"
                          label={displayLabel}
                          title={option}
                          size="small"
                          {...getTagProps({ index: tagIndex })}
                          key={`${option}-${tagIndex}`}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder={t("custom_filter.filter_value")}
                      size="small"
                      disabled={!filterItem.realOperator}
                      sx={customFilterFormStyles.textField}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  componentsProps={{
                    popper: {
                      onClick: (e: React.MouseEvent) => e.stopPropagation(),
                    },
                  }}
                  sx={{ width: "100%" }}
                />
              );
            })()
          ) : (
            <TextField
              placeholder={t("custom_filter.filter_value")}
              value={filterItem.value}
              variant="standard"
              onChange={(e) => handleValueChange(index, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              disabled={!filterItem.operator}
              sx={customFilterFormStyles.textField}
            />
          )}
        </Box>
      ))}

      {/* Action buttons */}
      <Box sx={customFilterFormStyles.actionButtonsContainer}>
        <Button
          startIcon={<span style={customFilterFormStyles.iconStyle}>+</span>}
          sx={customFilterFormStyles.actionButton}
          onClick={addFilter}
        >
          {t("custom_filter.add_filter")}
        </Button>

        <Button
          startIcon={
            <span style={customFilterFormStyles.iconStyle}>
              <DeleteForeverIcon fontSize="small" />
            </span>
          }
          sx={customFilterFormStyles.actionButton}
          onClick={handleClearAll}
        >
          {t("custom_filter.remove_all")}
        </Button>
        {/* Apply Filter Button */}
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          sx={customFilterFormStyles.applyButton}
        >
          {t("Common_options.Apply")}
        </Button>
      </Box>
    </Box>
  );
};

export default CustomFilterForm;
